import { useState, useCallback, useMemo } from 'react';
import {
  makeStyles,
  tokens,
  shorthands,
  mergeClasses,
  Badge,
  Button,
  Divider,
  Field,
  Input,
  Tooltip,
  Text,
  Spinner,
  useToastController,
  Toast,
  ToastTitle,
} from '@fluentui/react-components';
import {
  DismissRegular,
  EditRegular,
  CheckmarkRegular,
  PlayRegular,
  SparkleRegular,
  LockClosedRegular,
} from '@fluentui/react-icons';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useAgentChatAguiStore } from '@/stores/agentChatAguiStore';
import { useConnectorInfo, useConnectorContext } from '@/contexts';
import { actionNameKey } from '@/hooks/useWorkflowConnectors';
import { isAgentAction } from '@/types/workflowDefinition';
import { getBuiltInManifest } from '@/data/manifests';
import { MAX_NAME_LENGTH } from '@/utils/validation';
import type { Action } from '@/types/workflowDefinition';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  AgentVariantIcon,
  getAgentBadgeTokens,
  getAgentVariant,
  useAgentIconForegroundClass,
} from '../helpers/agentVariant';

const useStyles = makeStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground1,
    // Fixed height so toggling between display and edit modes never shifts
    // the rest of the panel.
    height: '64px',
    flexShrink: 0,
  },

  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    borderRadius: tokens.borderRadiusMedium,
    flexShrink: 0,
    overflow: 'hidden',
  },
  connectorIcon: {
    width: '28px',
    height: '28px',
    objectFit: 'contain',
  },
  defaultIcon: {
    fontSize: tokens.fontSizeBase600,
    color: tokens.colorNeutralForeground2,
  },
  agentIcon: {
    fontSize: tokens.fontSizeBase600,
  },

  titleSection: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    // Use no gap; let each row carry its own line-height for cleaner optical rhythm.
  },
  eyebrow: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    // Locked to the input "medium" height so the header doesn't grow when
    // toggling between display and edit modes.
    minHeight: '32px',
  },
  // Pencil is muted by default so the name dominates; lifts on hover/focus.
  editButton: {
    color: tokens.colorNeutralForeground3,
    transitionProperty: 'color, background-color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      color: tokens.colorNeutralForeground1,
    },
  },
  nodeName: {
    fontSize: tokens.fontSizeBase500,
    lineHeight: tokens.lineHeightBase500,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  nameButton: {
    // The name acts as a click target to start editing when not read-only.
    appearance: 'none',
    background: 'transparent',
    border: 'none',
    padding: 0,
    margin: 0,
    textAlign: 'left',
    cursor: 'text',
    minWidth: 0,
    color: 'inherit',
    font: 'inherit',
    // Tactile press feedback — subtle on heading-sized text.
    transitionProperty: 'transform, outline-color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':active': {
      transform: 'scale(0.98)',
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '2px',
      borderRadius: tokens.borderRadiusSmall,
    },
  },
  nameInput: {
    // Width sized to typical name length; never stretches the whole header.
    flex: '0 1 auto',
    width: '280px',
    maxWidth: '100%',
  },
  // Make the input read like editing the actual name text: same size + weight,
  // underline appearance with no chunky border. Height matches the medium
  // Input so the row doesn't grow when toggling into edit mode.
  nameInputControl: {
    '& input': {
      fontSize: tokens.fontSizeBase500,
      lineHeight: tokens.lineHeightBase500,
      fontWeight: tokens.fontWeightSemibold,
      color: tokens.colorNeutralForeground1,
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  nameCounter: {
    fontSize: tokens.fontSizeBase200,
    fontVariantNumeric: 'tabular-nums',
    color: tokens.colorNeutralForeground3,
    userSelect: 'none',
    pointerEvents: 'none',
  },
  nameCounterWarning: {
    color: tokens.colorPaletteDarkOrangeForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  nameCounterDanger: {
    color: tokens.colorPaletteRedForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  readOnlyBadge: {
    flexShrink: 0,
  },

  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    flexShrink: 0,
  },
  actionsDivider: {
    height: '20px',
    marginLeft: tokens.spacingHorizontalXXS,
    marginRight: tokens.spacingHorizontalXXS,
  },
});

/** Returns the editable name: innerActionName for tools, node name otherwise */
function getEditableName(node: { type: string; name: string; innerActionName?: string }): string {
  if (node.type === 'tool' && node.innerActionName) {
    return node.innerActionName;
  }
  return node.name;
}

const NAME_SOFT_CAP = 50;
const NAME_HARD_CAP = MAX_NAME_LENGTH;
// Show the live counter once the user is within ~25% of the hard cap,
// not from the first keystroke — keeps the input chrome clean for the
// common case (short names).
const NAME_COUNTER_REVEAL = Math.floor(NAME_HARD_CAP * 0.75);
const NAME_LONG_WARNING = 'Long names truncate on the canvas.';

// Build the Field validation props for the name input. Returns an empty
// object when there's nothing to surface so the spread is a no-op.
function getNameValidationProps(
  error: string,
  nameLength: number,
):
  | { validationState: 'error'; validationMessage: string }
  | { validationState: 'warning'; validationMessage: string }
  | Record<string, never> {
  if (error) return { validationState: 'error', validationMessage: error };
  if (nameLength > NAME_SOFT_CAP) {
    return { validationState: 'warning', validationMessage: NAME_LONG_WARNING };
  }
  return {};
}

// Pick the colour class for the live character counter based on how close
// the value is to the hard cap. Returns undefined under the soft cap so
// the counter stays neutral.
function getNameCounterClass(
  styles: { nameCounterDanger: string; nameCounterWarning: string },
  nameLength: number,
): string | undefined {
  if (nameLength >= NAME_HARD_CAP) return styles.nameCounterDanger;
  if (nameLength > NAME_SOFT_CAP) return styles.nameCounterWarning;
  return undefined;
}

/**
 * NodeEditorHeader - Header with node info and window controls
 */
export const NodeEditorHeader = () => {
  const styles = useStyles();
  const agentIconForeground = useAgentIconForegroundClass();
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editError, setEditError] = useState('');
  const [imageError, setImageError] = useState(false);
  const { dispatchToast } = useToastController();

  const {
    node,
    closeEditor,
    setNodeName,
    setInnerActionName,
  } = useNodeEditorStore();
  const isReadOnly = useNodeEditorStore((s) => s.isReadOnly);

  const renameAction = useWorkflowStore((s) => s.renameAction);
  const renameTrigger = useWorkflowStore((s) => s.renameTrigger);
  const renameToolInnerAction = useWorkflowStore((s) => s.renameToolInnerAction);

  const { isLoading } = useConnectorContext();
  const queuePrompt = useAgentChatAguiStore((s) => s.queuePrompt);
  const isStreaming = useAgentChatAguiStore((s) => s.isStreaming);
  const pendingPrompt = useAgentChatAguiStore((s) => s.pendingPrompt);
  const isAiBusy = isStreaming || pendingPrompt !== null;
  const isScopedEditEnabled = useFeatureFlag('scopedEdit');

  // Get connection reference from node data
  const nodeInputs = node?.nodeData && 'inputs' in node.nodeData
    ? node.nodeData.inputs as Record<string, unknown>
    : undefined;
  const hostConnection = (nodeInputs?.host as Record<string, unknown> | undefined)?.connection as Record<string, unknown> | undefined;
  const connectionRef = hostConnection?.referenceName as string | undefined;

  const connectorInfoByConnection = useConnectorInfo(connectionRef);
  // Per-action display fallback: useWorkflowConnectors keys the connector map
  // by namespaced action name too, so connection-less nodes still resolve their
  // connector icon / display name (no display fields are persisted on the action).
  // Tool nodes carry the parent tool name in `node.name`; the connector map keys
  // by the inner action name, which is the actual operation in `tool.actions`.
  const lookupActionName = node?.type === 'tool' ? (node.innerActionName ?? node.name) : node?.name;
  const connectorInfoByName = useConnectorInfo(actionNameKey(lookupActionName ?? ''));
  const connectorInfo = connectorInfoByConnection ?? connectorInfoByName;

  // Agents come in three flavors (native, Foundry, coding) that share
  // `type: 'Agent'` and the same manifest icon, so we use Fluent glyphs
  // (plus a custom coding glyph) + Otto agent badge tokens here to give
  // each variant a distinct color treatment.
  const agentAction =
    node?.type === 'action' && node.nodeData && isAgentAction(node.nodeData as Action)
      ? (node.nodeData as Action)
      : null;
  const isAgent = agentAction !== null;
  const agentVariant = agentAction ? getAgentVariant(agentAction) : null;

  // Built-in manifest fallback for non-agent operations without a connection.
  const builtInManifest = useMemo(() => {
    if (connectorInfo || isAgent) return null;
    if (!node?.nodeData?.type) return null;
    return getBuiltInManifest(node.nodeData.type);
  }, [connectorInfo, isAgent, node?.nodeData?.type]);

  const handleStartEdit = useCallback(() => {
    if (!node) return;
    setEditName(getEditableName(node));
    setIsEditing(true);
  }, [node]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditName('');
    setEditError('');
  }, []);

  const handleSaveEdit = useCallback(() => {
    if (!node || !editName.trim()) {
      handleCancelEdit();
      return;
    }

    // For tools, compare against inner action name; for others, compare against node name
    const currentName = getEditableName(node);

    // If name hasn't changed, just close
    if (editName.trim() === currentName) {
      handleCancelEdit();
      return;
    }

    // Call the appropriate rename function based on node type
    let result;
    if (node.type === 'trigger') {
      result = renameTrigger(node.name, editName.trim());
    } else if (node.type === 'tool' && node.parentAgentName && node.toolName && node.innerActionName) {
      result = renameToolInnerAction(node.parentAgentName, node.toolName, node.innerActionName, editName.trim());
    } else {
      result = renameAction(node.name, editName.trim());
    }

    if (!result.isValid) {
      setEditError(result.error ?? 'Invalid name');
      dispatchToast(
        <Toast>
          <ToastTitle>Rename failed: {result.error}</ToastTitle>
        </Toast>,
        { intent: 'error' }
      );
      return;
    }

    // Update the node editor's reference to the new name
    if (result.normalizedName) {
      if (node.type === 'tool') {
        setInnerActionName(result.normalizedName);
      } else {
        setNodeName(result.normalizedName);
      }
    }

    handleCancelEdit();
  }, [node, editName, renameAction, renameTrigger, renameToolInnerAction, setNodeName, setInnerActionName, handleCancelEdit, dispatchToast]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Enter' && e.key !== 'Escape') return;
    // Stop propagation so the modal's Dialog doesn't also receive the
    // keystroke (which would close the editor entirely).
    e.preventDefault();
    e.stopPropagation();
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else {
      handleCancelEdit();
    }
  }, [handleSaveEdit, handleCancelEdit]);

  const handleAskOtto = useCallback(() => {
    if (!node || isAiBusy) return;
    const displayName = getEditableName(node);
    queuePrompt(
      `Help me configure the "${displayName}" ${node.type === 'trigger' ? 'trigger' : 'action'}`,
      { type: 'single', nodeNames: [node.name] },
      'node-editor-header',
    );
  }, [node, isAiBusy, queuePrompt]);

  if (!node) return null;

  const nodeType = node.nodeData?.type ?? node.type;
  const displayType = connectorInfo?.displayName ?? nodeType;
  const agentBadge = agentVariant ? getAgentBadgeTokens(agentVariant) : null;
  const brandColor =
    agentBadge?.background
    ?? connectorInfo?.brandColor
    ?? builtInManifest?.properties.brandColor
    ?? tokens.colorNeutralBackground4;
  // Agents always render the Fluent glyph below — skip the manifest icon so
  // the Foundry / native / coding split stays visible (manifests share one SVG).
  const iconUri = isAgent
    ? undefined
    : connectorInfo?.iconUri ?? builtInManifest?.properties.iconUri;

  const AGENT_TYPE_LABEL: Record<'native' | 'foundry' | 'coding' | 'hosted', string> = {
    native: 'AI Agent',
    foundry: 'AI Agent (Foundry)',
    coding: 'AI Agent (Coding)',
    hosted: 'AI Agent (Hosted)',
  };
  const typeLabel = agentVariant ? AGENT_TYPE_LABEL[agentVariant] : displayType;

  const displayName = getEditableName(node);
  const nameElement = <Text className={styles.nodeName}>{displayName}</Text>;

  return (
    <div className={styles.header}>
      {/* Icon — tooltip surfaces the operation type without crowding the layout */}
      <Tooltip content={typeLabel} relationship="label" positioning="below">
        <div
          className={styles.iconContainer}
          style={{ backgroundColor: brandColor }}
          role="img"
          aria-label={typeLabel}
        >
          {isLoading && connectionRef && !connectorInfo ? (
            <Spinner size="tiny" />
          ) : iconUri && !imageError ? (
            <img
              src={iconUri}
              alt=""
              className={styles.connectorIcon}
              onError={() => setImageError(true)}
            />
          ) : agentVariant ? (
            <span
              aria-hidden="true"
              className={mergeClasses(styles.agentIcon, agentIconForeground(agentVariant))}
            >
              <AgentVariantIcon variant={agentVariant} />
            </span>
          ) : (
            <PlayRegular aria-hidden="true" className={styles.defaultIcon} />
          )}
        </div>
      </Tooltip>

      {/* Title section */}
      <div className={styles.titleSection}>
        <Text className={styles.eyebrow}>
          {node.type === 'trigger'
            ? 'Trigger details'
            : node.type === 'tool'
              ? 'Tool details'
              : 'Action details'}
        </Text>
        <div className={styles.nameRow}>
          {isEditing ? (
            <>
              <Field
                className={styles.nameInput}
                {...getNameValidationProps(editError, editName.length)}
              >
                <Input
                  className={styles.nameInputControl}
                  appearance="underline"
                  value={editName}
                  maxLength={NAME_HARD_CAP}
                  onChange={(_, data) => {
                    setEditName(data.value);
                    setEditError(''); // Clear error on edit
                  }}
                  onKeyDown={handleKeyDown}
                  size="medium"
                  autoFocus
                  aria-label="Edit node name"
                  aria-invalid={!!editError}
                  {...(editName.length >= NAME_COUNTER_REVEAL && {
                    contentAfter: (
                      <span
                        className={mergeClasses(
                          styles.nameCounter,
                          getNameCounterClass(styles, editName.length),
                        )}
                        aria-live="polite"
                        aria-label={`${editName.length} of ${NAME_HARD_CAP} characters`}
                      >
                        {editName.length}/{NAME_HARD_CAP}
                      </span>
                    ),
                  })}
                />
              </Field>
              <Tooltip content="Save (Enter)" relationship="label">
                <Button
                  appearance="primary"
                  size="small"
                  icon={<CheckmarkRegular />}
                  onClick={handleSaveEdit}
                  aria-label="Save name"
                />
              </Tooltip>
              <Tooltip content="Cancel (Esc)" relationship="label">
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<DismissRegular />}
                  onClick={handleCancelEdit}
                  aria-label="Cancel editing"
                />
              </Tooltip>
            </>
          ) : (
            <>
              {isReadOnly ? nameElement : (
                <button
                  type="button"
                  className={styles.nameButton}
                  onClick={handleStartEdit}
                  aria-label={`Rename ${node.type}, currently ${displayName}`}
                >
                  {nameElement}
                </button>
              )}
              {!isReadOnly && (
                <Tooltip content="Rename" relationship="label">
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<EditRegular />}
                    onClick={handleStartEdit}
                    aria-label="Edit node name"
                    className={styles.editButton}
                  />
                </Tooltip>
              )}
              {isReadOnly && (
                <Tooltip
                  content="Switch to draft mode to edit this workflow."
                  relationship="description"
                >
                  <Badge
                    appearance="tint"
                    color="warning"
                    size="small"
                    icon={<LockClosedRegular />}
                    className={styles.readOnlyBadge}
                  >
                    Read-only
                  </Badge>
                </Tooltip>
              )}
            </>
          )}
        </div>
      </div>

      {/* Window controls */}
      <div className={styles.actions}>
        {!isReadOnly && isScopedEditEnabled && (
          <Tooltip content="Configure with Copilot" relationship="label">
            <Button
              appearance="subtle"
              size="small"
              icon={<SparkleRegular />}
              onClick={handleAskOtto}
              disabled={isAiBusy}
              aria-label="Configure with Copilot"
            />
          </Tooltip>
        )}
        {!isReadOnly && isScopedEditEnabled && (
          <Divider vertical className={styles.actionsDivider} />
        )}
        <Tooltip content="Close" relationship="label">
          <Button
            appearance="subtle"
            size="small"
            icon={<DismissRegular />}
            onClick={closeEditor}
            aria-label="Close editor"
          />
        </Tooltip>
      </div>
    </div>
  );
};

NodeEditorHeader.displayName = 'NodeEditorHeader';
